export default {
  // Module Title
  title: 'Form Management',
  subtitle: 'Design forms, configure processes, publish versions',
  
  // Navigation
  nav: {
    overview: 'Overview',
    definitions: 'Form Definitions',
    myInstances: 'My Instances',
    review: 'Version Review',
    templates: 'Templates',
    translations: 'Translations',
    statistics: 'Statistics',
    settings: 'Settings',
  },
  
  // Overview Page
  overview: {
    title: 'Form Management Overview',
    description: 'Design forms, configure approval processes, manage version releases',
    stats: {
      totalForms: 'Total Forms',
      activeForms: 'Published',
      draftForms: 'Drafts',
      pendingReview: 'Pending Review',
      templates: 'Templates',
    },
    quickActions: {
      createForm: 'Create Form',
      createFormDesc: 'Create a new form from scratch',
      useTemplate: 'Use Template',
      useTemplateDesc: 'Quickly create from template',
      manageForms: 'Manage Forms',
      manageFormsDesc: 'View and edit existing forms',
      startUsing: 'Start',
      title: 'Quick Actions',
    },
    recentForms: 'Recent Edits',
    viewAll: 'View All',
    tipTitle: 'Form + Process Integrated Design',
    tipDesc: 'Complete form field design and approval process configuration in the designer. After publishing, users can initiate applications in the Approval Center.',
    pendingDesign: 'Pending Design',
    goToReview: 'Review',
    formNameColumn: 'Form Name',
    lastModifiedColumn: 'Last Modified',
  },
  
  // Form Definition
  definition: {
    title: 'Form Definitions',
    description: 'Manage all form definitions, versions, and configurations',
    createForm: 'Create Form',
    searchPlaceholder: 'Search form name or key...',
    formInfo: 'Form Info',
    key: 'Key',
    category: 'Category',
    status: 'Status',
    updatedAt: 'Updated At',
    actions: 'Actions',
    noForms: 'No forms yet',
    createFirst: 'Create your first form',
    // Actions
    viewDetail: 'View Details',
    designForm: 'Design Form',
    editInfo: 'Edit Info',
    versionManage: 'Manage Versions',
    clone: 'Clone',
    archive: 'Archive',
    delete: 'Delete',
    disable: 'Disable',
    enable: 'Enable',
    // Status
    statusLabels: {
      DRAFT: 'Draft',
      PUBLISHED: 'Published',
      DISABLED: 'Disabled',
      ARCHIVED: 'Archived',
    },
    // Categories
    categories: {
      all: 'All Categories',
      HR: 'Human Resources',
      FINANCE: 'Finance',
      IT: 'IT',
      ADMIN: 'Administration',
      OTHER: 'Other',
    },
    // Confirm Actions
    confirmDelete: 'Are you sure you want to delete form "{name}"? This action cannot be undone.',
    confirmArchive: 'Are you sure you want to archive form "{name}"? This action cannot be undone.',
    onlyDraftCanDelete: 'Only draft forms can be deleted',
    allOrganizations: 'All Organizations',
    platformForms: 'Platform Forms',
    cloneFeatureComingSoon: 'Clone feature coming soon',
  },

  // Translations
  translationsPage: {
    emptyTitle: 'No translations',
    emptyDesc: 'No translations for this version yet',
    emptyAction: 'Add first translation',
    emptyKeysDesc: 'No translation keys yet. Add one above.',
  },
  
  // Create Form
  create: {
    title: 'Create Form',
    belongOrganization: 'Belong to Organization',
    platformLevelForm: 'Platform-level universal form (available to all organizations)',
    organizationHint: 'If an organization is selected, the form will only be visible to members of that organization; leave empty for platform-level universal form',
    description: 'Define basic form information, then enter the designer to configure fields and processes',
    blankCreate: 'Blank Create',
    blankCreateDesc: 'Start from scratch, freely design form structure',
    useTemplate: 'Use Template',
    useTemplateDesc: 'Quickly create from predefined templates',
    basicInfo: 'Basic Information',
    formName: 'Form Name',
    formNamePlaceholder: 'e.g., Expense Claim Form',
    formKey: 'Form Key',
    formKeyPlaceholder: 'e.g., expense_claim',
    formKeyHint: 'Unique identifier, only lowercase letters, numbers and underscores, starting with a letter',
    formDescription: 'Form Description',
    formDescriptionPlaceholder: 'Describe the purpose and usage scenarios of this form...',
    approvalConfig: 'Approval Configuration',
    requiresApproval: 'Requires Approval Process',
    requiresApprovalHint: 'When enabled, submitted forms will enter the approval process',
    approvalTip: 'After creating the form, you can configure approval nodes and process logic in the "Process Design" tab of the designer.',
    createAndDesign: 'Create and Enter Designer',
    creating: 'Creating...',
    keyFormatError: 'Form Key can only contain lowercase letters, numbers and underscores, and must start with a letter',
    advancedOptions: 'Advanced Options',
    autoGeneratedKey: 'Form Key',
    keyAutoGenerated: 'A unique identifier will be auto-generated',
    keyCustomHint: 'Leave empty for auto-generation. Custom keys must start with a letter and contain only letters, numbers, underscores',
  },
  
  // Version Review
  review: {
    title: 'Version Review',
    description: 'Review form versions submitted by designers, auto-publish on approval',
    pending: 'Pending Review',
    submittedBy: 'Submitted By',
    submittedAt: 'Submitted At',
    submissionNote: 'Submission Note',
    preview: 'Preview',
    approve: 'Approve',
    reject: 'Reject',
    approveTitle: 'Approve Version',
    rejectTitle: 'Reject Version',
    approveDesc: 'After approval, the version will be automatically published and set as default.',
    rejectDesc: 'Please provide rejection reason to help the designer improve.',
    reviewComment: 'Review Comment',
    rejectReason: 'Rejection Reason',
    rejectReasonPlaceholder: 'Please explain the reason, e.g., field naming issues, missing validation...',
    confirmApprove: 'Confirm Approval',
    confirmReject: 'Confirm Rejection',
    noItems: 'No pending reviews',
    noItemsDesc: 'Versions submitted by designers will appear here',
    snapshotId: 'Snapshot ID',
    formVersionId: 'Form Version ID',
    processVersionId: 'Process Version ID',
    formSchema: 'Form Schema',
    processModel: 'Process Model',
    previewUser: 'Preview User',
    rdDept: 'R&D',
    productDept: 'Product',
    previewDataLogged: 'Preview data logged to console',
    approvedAndPublished: 'Approved and Published!',
    statusRejected: 'Rejected',
    showOnlyMine: 'Show only mine',
    filteredCount: 'Filtered: {count} items',
    refresh: 'Refresh',
    noMyPending: 'No pending reviews from you',
    mySubmittedWillShowHere: 'Your submitted form designs will appear here',
    showAll: 'Show All',
    mine: 'Mine',
    previewFormAndProcess: 'Preview form design and approval process',
    formPreview: 'Form Preview',
    submitPreview: 'Submit Preview',
    noFormSchema: 'No form schema available',
    approvalProcessPreview: 'Approval Process Preview',
    processAccordingToWorkflow: 'Form will be processed according to the above workflow',
    noApprovalRequired: 'This form does not require approval',
    technicalDetails: 'Technical Details',
  },
  
  // Publish (Activate)
  publish: {
    title: 'Activate Version',
    description: 'After activation, this version becomes the current default version for users in the Approval Center.',
    approved: 'Version approved. Activate as current version now?',
    releaseNote: 'Version Note',
    releaseNotePlaceholder: 'Record the main changes in this version...',
    publishNow: 'Activate Now',
    publishLater: 'Activate Later',
    publishing: 'Activating...',
    publishSuccess: 'Activated successfully! New version is now live',
  },
  
  // Snapshot/Version Status (simplified: approval = publish, no separate APPROVED status)
  snapshotStatus: {
    DRAFT: 'Draft',
    PENDING: 'Pending Review',
    PENDING_REVIEW: 'Pending Review',
    PUBLISHED: 'Published',
    ACTIVE: 'Published',
    REJECTED: 'Rejected',
    DEPRECATED: 'Deprecated',
    ARCHIVED: 'Archived',
    SUSPENDED: 'Suspended',
  },
  
  // Version
  version: {
    title: 'Version Management',
    list: 'Version List',
    create: 'Create New Version',
    compare: 'Compare Versions',
    // Version Status (simplified: approval = publish)
    status: {
      DRAFT: 'Draft',
      PENDING_REVIEW: 'Pending Review',
      PUBLISHED: 'Published',
      REJECTED: 'Rejected',
      DEPRECATED: 'Deprecated',
    },
    isDefault: 'Default Version',
    setDefault: 'Set as Default',
    publish: 'Publish Version',
    deprecate: 'Deprecate Version',
    submitReview: 'Submit for Review',
    versionName: 'Version Name',
    releaseNote: 'Release Note',
  },
  
  // My Submissions
  submissions: {
    title: 'My Submissions',
    description: 'View my submitted version reviews and their status',
    status: {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      all: 'All',
    },
    resubmit: 'Resubmit',
    edit: 'Edit',
    viewHistory: 'History',
    reviewHistory: 'Review History',
    rejectedReason: 'Rejection Reason',
    noRecords: 'No submission records',
    noRecordsDesc: 'Submit a version for review and records will appear here',
    goDesign: 'Design a Form',
    submittedAt: 'Submitted at',
    reviewedAt: 'Reviewed at',
  },
  
  // Form Instance
  instance: {
    title: 'My Form Instances',
    description: 'View and manage all my submitted form instances',
    businessKey: 'Business Key',
    region: 'Region',
    status: {
      DRAFT: 'Draft',
      SUBMITTED: 'Submitted',
      PENDING_APPROVAL: 'Pending Approval',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      WITHDRAWN: 'Withdrawn',
      CANCELLED: 'Cancelled',
    },
    actions: {
      view: 'View Details',
      edit: 'Edit',
      submit: 'Submit',
      withdraw: 'Withdraw',
      resubmit: 'Resubmit',
      saveDraft: 'Save Draft',
      cancel: 'Cancel',
      delete: 'Delete',
    },
    noInstances: 'No form instances',
    submittedAt: 'Submitted At',
    createdAt: 'Created At',
    approvalStatus: 'Approval Status',
    withdrawConfirm: 'Are you sure you want to withdraw this application?',
    withdrawReason: 'Withdrawal Reason',
    withdrawReasonPlaceholder: 'Enter withdrawal reason (optional)',
    withdrawSuccess: 'Withdrawn successfully',
    withdrawFailed: 'Failed to withdraw',
    notFound: 'Form instance not found',
    defaultTitle: 'Form Instance',
    statusMessages: {
      pendingApproval: 'This form is pending approval. You can withdraw this application.',
      approved: 'This form has been approved.',
      cancelled: 'This form has been cancelled.',
      cannotEdit: 'This form cannot be edited in current status.',
    },
    approvalInfo: 'Approval Info',
    approvalStatusLabel: 'Approval Status',
    processing: 'Processing',
    startTime: 'Start Time',
    viewApprovalDetails: 'View Approval Details',
    instanceInfo: 'Instance Info',
    instanceId: 'Instance ID',
    formVersion: 'Form Version',
    lastUpdated: 'Last Updated',
    createdBy: 'Created By',
    
    // Form Submit Page
    loadingForm: 'Loading form...',
    formNotFound: 'Form Not Found',
    formNotFoundDesc: 'The form was not found. It may have been deleted or you do not have access.',
    backToApproval: 'Back to Approval Center',
    back: 'Back',
    formIncomplete: 'Form design is incomplete, cannot fill out',
    draftUpdated: 'Draft updated',
    draftSaved: 'Draft saved',
    saveFailed: 'Save failed',
    submitSuccess: 'Form submitted successfully, approval process initiated!',
    submitFailed: 'Submit failed',
    submitting: 'Submitting...',
    saveDraft: 'Save Draft',
    saving: 'Saving...',
    pleaseSelectDepartment: 'Please select submitting department',
    createDraftFailed: 'Failed to create draft: invalid response',
    createInstanceFailed: 'Failed to create instance: invalid response',
    unknownError: 'Unknown error',
    loadFormFailed: 'Failed to load form',
    
    // Approval Process Preview
    approvalProcessPreview: 'Approval Process Preview',
    approvalProcess: 'Approval Process',
    loadingProcessPreview: 'Loading process preview...',
    processPreviewHint: '💡 Upon submission, the form will follow the above approval process',
    processPreviewIncomplete: 'Please fill in required information to view the full approval process.',
    currentUser: 'Current User',
  },
  
  // Template
  template: {
    title: 'Form Templates',
    description: 'Use predefined templates to quickly create forms',
    create: 'Create Template',
    public: 'Public',
    builtin: 'Built-in',
    useTemplate: 'Use This Template',
    createFromTemplate: 'Create from Template',
    noTemplates: 'No templates yet',
    categories: {
      all: 'All Categories',
      HR: 'Human Resources',
      FINANCE: 'Finance',
      IT: 'IT',
      ADMIN: 'Administration',
      OTHER: 'Other',
    },
  },
  
  // Templates Page (/forms/templates)
  templates: {
    title: 'Form Templates',
    searchPlaceholder: 'Search templates...',
    create: 'Create Template',
    preview: 'Preview',
    use: 'Use',
    builtin: 'Built-in',
    public: 'Public',
    noTemplates: 'No templates available',
    noTemplatesDesc: 'Create your first template',
  },
  
  // Translation
  translation: {
    title: 'Translation Management',
    description: 'Manage multi-language translations for form versions',
    selectForm: 'Select Form',
    selectVersion: 'Select Version',
    defaultLocale: 'Default Language',
    supportedLocales: 'Supported Languages',
    addTranslation: 'Add Translation',
    editTranslation: 'Edit Translation',
    deleteTranslation: 'Delete Translation',
    batchImport: 'Batch Import',
    completeness: 'Completeness',
    coverage: 'Coverage',
    complete: 'Complete',
    incomplete: 'Incomplete',
    missingKeys: 'Missing',
    noTranslations: 'No translations for this version',
    addFirst: 'Add first translation',
    translationContent: 'Translation Content',
    addKey: 'Add Key',
    noContent: 'No content yet, click button above to add',
    languages: {
      'zh-CN': 'Simplified Chinese',
      'zh-TW': 'Traditional Chinese',
      'en-US': 'English',
      'ja-JP': 'Japanese',
      'ko-KR': 'Korean',
    },
    import: {
      title: 'Batch Import Translations',
      description: 'Import translations in JSON format',
      formatExample: 'JSON Format Example',
      placeholder: 'Paste JSON translation data...',
      button: 'Import',
      success: 'Import successful',
    },
  },
  
  // Designer
  designer: {
    title: 'Form Designer',
    loading: {
      processDesigner: 'Loading process designer...',
    },
    tabs: {
      form: 'Form Design',
      process: 'Process Design',
      preview: 'Preview',
    },
    fieldPalette: 'Field Palette',
    designCanvas: 'Design Canvas',
    dragToAdd: 'Drag to add field',
    dropHere: 'Drop field here',
    noFieldSelected: 'No field selected',
    selectFieldToEdit: 'Select a field to edit its properties',
    fieldTypes: {
      text: 'Text Input',
      textarea: 'Text Area',
      number: 'Number',
      money: 'Amount',
      email: 'Email',
      phone: 'Phone',
      url: 'URL',
      select: 'Dropdown',
      multiSelect: 'Multi Select',
      radio: 'Radio Group',
      checkbox: 'Checkbox',
      member: 'Member',
      department: 'Department',
      date: 'Date Picker',
      dateRange: 'Date Range',
      time: 'Time Picker',
      datetime: 'Date Time',
      file: 'File Upload',
      image: 'Image Upload',
      richText: 'Rich Text',
      signature: 'Signature',
      seal: 'Electronic Seal',
      location: 'Location',
      rating: 'Rating',
      switch: 'Switch',
      slider: 'Slider',
      color: 'Color Picker',
      hidden: 'Hidden Field',
      detail: 'Subtable',
      container: 'Layout Container',
      group: 'Group',
      serialNumber: 'Serial Number',
      relatedForm: 'Related Form',
      relatedQuery: 'Related Query',
      address: 'Address',
      region: 'Country/Region',
      cascade: 'Cascade Select',
      imageText: 'Image & Text',
      wordTemplate: 'Word Template',
      currency: 'Currency',
    },
    fieldGroups: {
      basic: 'Basic Fields',
      advanced: 'Advanced Fields',
      layout: 'Layout Components',
    },
    defaults: {
      input: 'Please enter',
      select: 'Please select',
      number: 'Please enter a number',
      amount: 'Please enter amount',
      member: 'Select member',
      department: 'Select department',
      date: 'Select date',
      location: 'Click to get location',
      related: 'Select related data',
      address: 'Enter address',
      optionLabel: 'Option {index}',
      groupTitle: 'Group Title',
    },
    palette: {
      searchPlaceholder: 'Search components',
      noMatch: 'No matching components',
      categories: {
        basic: 'Basic',
        advanced: 'Advanced',
      },
    },
    properties: {
      basic: 'Basic Properties',
      fieldKey: 'Field Key',
      fieldKeyPlaceholder: 'Unique identifier, e.g., firstName',
      label: 'Label',
      labelPlaceholder: 'Display label text',
      placeholder: 'Placeholder',
      placeholderPlaceholder: 'Input hint text',
      defaultValue: 'Default Value',
      helpText: 'Help Text',
      validation: 'Validation',
      required: 'Required',
      minLength: 'Min Length',
      maxLength: 'Max Length',
      pattern: 'Pattern (Regex)',
      style: 'Style',
      width: 'Width',
      options: 'Options',
      addOption: 'Add Option',
      optionLabel: 'Option Label',
      optionValue: 'Option Value',
    },
    status: {
      draft: 'Draft',
      pendingReview: 'Pending Review',
      published: 'Published',
      rejected: 'Rejected',
      deprecated: 'Deprecated',
    },
    actions: {
      save: 'Save',
      saving: 'Saving...',
      saveAsDraft: 'Save as Draft',
      preview: 'Preview',
      submitReview: 'Submit for Review',
      confirmSubmit: 'Confirm Submit',
      submitting: 'Submitting...',
      submitPreview: 'Submit Preview',
      publish: 'Publish',
      createNewVersion: 'Create New Version',
      creating: 'Creating...',
      undo: 'Undo',
      redo: 'Redo',
      clear: 'Clear Canvas',
    },
    process: {
      enableApproval: 'Enable Approval Process',
      enableApprovalHint: 'Forms will automatically start approval process after submission',
      statusEnabled: 'Enabled - Submissions require approval',
      statusDisabled: 'Disabled - Submissions complete directly',
      active: 'Approval process active',
      helpEnabled: 'Configure approval nodes and flow below',
      helpDisabled: 'Enable to configure approval process',
      disabledHint: 'When disabled, form submissions will be completed directly without approval',
      noProcess: 'Approval Process Disabled',
      noProcessDesc: 'Submitted forms will be processed directly. To add an approval process, enable the "Enable Approval Process" switch above.',
      enableButton: 'Enable Approval Process',
    },
    noFields: 'No fields yet, please add fields in form design mode first',
    previewTitle: 'Form Preview',
    processPreviewTitle: 'Approval Process Preview',
    processPreviewHint: 'Submitted forms will follow this approval process',
    noProcessWarning: 'Approval process enabled but no nodes configured',
    configProcess: 'Configure Process',
    canvas: {
      ratingUnit: 'pts',
      fileUpload: {
        title: 'Click or drag files to upload',
        hint: 'Supports common file formats',
      },
      imageUpload: {
        title: 'Click or drag images to upload',
        hint: 'Supports JPG, PNG, GIF',
      },
      memberPlaceholder: 'Select member',
      departmentPlaceholder: 'Select department',
      locationPlaceholder: 'Click to get location',
      serialNumberPlaceholder: 'Auto generated',
      relatedPlaceholder: 'Select related data',
      addressRegionPlaceholder: 'Select province/city/district',
      addressDetailPlaceholder: 'Detailed address',
      regionPlaceholder: 'Select country/region',
      cascadePlaceholder: 'Select',
      signature: {
        title: 'Click here to sign',
        hint: 'Signature will be saved as image',
      },
      seal: {
        title: 'Select electronic seal',
        hint: 'Supports multiple seal types',
      },
      richTextPlaceholder: 'Enter rich text here...',
      wordTemplate: {
        title: 'Word Template',
        action: 'Click to configure template',
      },
      currencyPlaceholder: 'Please enter amount',
      unknownPlaceholder: '--',
      empty: {
        dropTitle: 'Release to add component',
        dropSubtitle: 'Place component into the form',
        idleTitle: 'Drag components here from the left',
        idleSubtitle: 'Start designing your form',
      },
      toolbar: {
        undo: 'Undo (Ctrl+Z)',
        redo: 'Redo (Ctrl+Y)',
        history: 'History',
        search: 'Search',
        shortcuts: 'Shortcuts',
      },
      actions: {
        duplicate: 'Duplicate',
        delete: 'Delete',
      },
      datePlaceholder: 'Select date',
      timePlaceholder: 'Select time',
      dateRangeStartPlaceholder: 'Start date',
      dateRangeEndPlaceholder: 'End date',
      dateRangeTo: 'to',
      multiSelectPlaceholder: 'Select (multiple)',
    },
    detail: {
      actions: {
        tableActions: 'Actions',
        delete: 'Delete',
        addItem: 'Add item',
        batchImport: 'Batch import',
      },
      dialog: {
        editColumnTitle: 'Edit column properties',
      },
      dropzone: {
        invalidTitle: 'Unsupported component',
        invalidDesc: 'Subtable does not support nested containers or subtables',
        validTitle: 'Release to add column',
        validDesc: 'A new subtable column will be created',
        idleTitle: 'Drag components here from the left',
        idleDesc: 'Add columns to the subtable',
      },
      column: {
        edit: 'Edit',
        delete: 'Delete',
      },
      placeholders: {
        input: 'Please enter',
        textarea: 'Please enter',
        number: 'Please enter a number',
        select: 'Please select',
        date: 'Select date',
        member: 'Select member',
        department: 'Select department',
        upload: 'Click to upload',
        default: '--',
      },
      editor: {
        columnTitle: 'Column Title',
        fieldKey: 'Field Key',
        optionsPerLine: 'Options (one per line)',
        optionsPlaceholder: 'Option 1\nOption 2\nOption 3',
        required: 'Required',
        done: 'Done',
      },
    },
    propertyPanel: {
      emptyHint: 'Select a node on the canvas to edit',
      detailColumnPrefix: 'Detail Column · {label}',
      tabs: {
        formSettings: 'Form Settings',
        pageProperties: 'Page Properties',
      },
      formSettings: {
        placeholder: 'Form-level settings',
        developing: '(In development)',
      },
      labels: {
        componentId: 'Component ID',
        title: 'Title',
        placeholder: 'Placeholder',
        description: 'Description',
        defaultValue: 'Default Value',
        defaultAmount: 'Default Amount',
        status: 'Status',
        required: 'Required',
        optionsSettings: 'Options',
        minLength: 'Min Length',
        maxLength: 'Max Length',
        minValue: 'Min Value',
        maxValue: 'Max Value',
        precision: 'Precision',
        prefix: 'Prefix',
        suffix: 'Suffix',
        defaultCurrency: 'Default Currency',
        allowCurrencySelect: 'Currency Selector',
        allowedCurrencies: 'Allowed Currencies',
        minRows: 'Min Rows',
        maxRows: 'Max Rows',
        layout: 'Layout',
        columnRatio: 'Column Ratio',
        columnGap: 'Column Gap',
        rowGap: 'Row Gap',
      },
      placeholders: {
        input: 'Please enter',
        description: 'Field description',
        number: 'Please enter a number',
        select: 'Please select',
        defaultValue: 'Enter default value',
        amount: 'Enter amount',
        option: 'Option',
        noLimit: 'No limit',
        currencyPrecision: '2',
        prefixExample: 'e.g. $',
        suffixExample: 'e.g. USD',
        columnRatioExample: 'e.g. 1:2:1',
        minRows: '0',
      },
      statusOptions: {
        normal: 'Normal',
        disabled: 'Disabled',
        readonly: 'Read-only',
        hidden: 'Hidden',
      },
      yesNo: {
        yes: 'Yes',
        no: 'No',
      },
      options: {
        listTitle: 'Options',
        add: 'Add',
        emptyHint: 'Add options above',
      },
      select: {
        noneLabel: 'None',
      },
      currency: {
        cny: 'CNY (Chinese Yuan)',
        usd: 'USD (US Dollar)',
        eur: 'EUR (Euro)',
        jpy: 'JPY (Japanese Yen)',
        gbp: 'GBP (British Pound)',
        hkd: 'HKD (Hong Kong Dollar)',
        cnyShort: 'CNY',
        usdShort: 'USD',
        eurShort: 'EUR',
        jpyShort: 'JPY',
        gbpShort: 'GBP',
        hkdShort: 'HKD',
      },
      layoutPresets: {
        col1: '1 Column',
        col2Equal: '1:1',
        col2LeftWide: '2:1',
        col2RightWide: '1:2',
        col3Equal: '1:1:1',
        col3LeftWide: '2:1:1',
        col3MiddleWide: '1:2:1',
        col4Equal: '1:1:1:1',
      },
      gaps: {
        none: 'None (0px)',
        small: 'Small (8px)',
        medium: 'Medium (16px)',
        large: 'Large (24px)',
      },
      detailHint: '💡 Click the \"Add Column\" button on the subtable in the canvas to configure columns',
      containerHint: '💡 Drag components into each column from the left',
    },
    group: {
      placeholder: 'Group',
      developing: 'Group designer in development...',
    },
    container: {
      placeholder: 'Layout Container',
      columns: '{count} columns',
      column: 'Column {index}',
      developing: 'Layout container designer in development...',
    },
    preview: {
      submitterName: 'Preview User',
      departments: {
        dev: 'Development',
        product: 'Product',
      },
    },
    permission: {
      title: 'No Access to Form Designer',
      description: 'You do not have permission to access this page. Required permissions: {permissions}',
    },
    dialog: {
      submitTitle: 'Submit Version for Review',
      submitDescription: 'After submission, administrators will review this version. It can only be published after approval.',
      submitNoteLabel: 'Submission Notes (Optional)',
      submitNotePlaceholder: 'Describe what changed in this version...',
      createVersionDescription: 'The current version is published. Create a new version to continue editing. The new version will be based on the current design.',
      createVersionHint: 'Create v{next} based on v{current}',
    },
    messages: {
      loadFailed: 'Load failed: {message}',
      saveSuccess: 'Saved successfully',
      saveFailed: 'Save failed: {message}',
      submitReviewSuccess: 'Submitted for review. Please wait for approval.',
      submitReviewFailed: 'Submit review failed: {message}',
      newVersionCreated: 'New version v{version} created',
      createVersionFailed: 'Create version failed: {message}',
      previewSubmit: 'Form data has been printed to the console',
    },
  },

  fieldRenderer: {
    address: {
      provincePlaceholder: 'Province/State',
      cityPlaceholder: 'City',
      districtPlaceholder: 'District',
      detailPlaceholder: 'Detailed address',
    },
    region: {
      placeholder: 'Select country/region',
    },
    serialNumber: {
      placeholder: 'Auto-generated on submit',
    },
    signature: {
      clear: 'Clear',
      signed: 'Signed',
      hint: 'Draw your signature in the area above',
    },
    richtext: {
      loading: 'Loading…',
      bold: 'Bold',
      italic: 'Italic',
      strike: 'Strikethrough',
      bulletList: 'Bullet list',
      orderedList: 'Ordered list',
      blockquote: 'Quote',
      undo: 'Undo',
      redo: 'Redo',
      placeholder: 'Enter text',
    },
    cascade: {
      placeholder: 'Select',
      empty: 'No options configured',
    },
  },

  // Common
  common: {
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    view: 'View',
    search: 'Search',
    filter: 'Filter',
    status: 'Status',
    optional: 'Optional',
    allStatus: 'All Status',
    allCategories: 'All Categories',
    actions: 'Actions',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    close: 'Close',
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    info: 'Info',
    yes: 'Yes',
    no: 'No',
    page: 'Page {current} of {total}',
    totalItems: 'Total {total} items',
    previousPage: 'Previous',
    nextPage: 'Next',
    noData: 'No Data',
    processing: 'Processing...',
    confirmDelete: 'Are you sure you want to delete? This action cannot be undone.',
  },
  
  // Statistics
  statistics: {
    title: 'Statistics',
    subtitle: 'Form usage and data analysis',
    description: 'View statistics on form submissions, approvals, and usage',
    timeRange: {
      last7Days: 'Last 7 Days',
      last30Days: 'Last 30 Days',
      last90Days: 'Last 90 Days',
      last1Year: 'Last 1 Year',
    },
    cards: {
      totalForms: 'Total Forms',
      totalSubmissions: 'Total Submissions',
      approvalRate: 'Approval Rate',
      avgProcessingTime: 'Avg Processing Time',
      activeForms: 'Published',
      activeUsers: 'Active Users',
      processingCycle: 'Processing Cycle',
      stable: 'Stable',
    },
    topForms: {
      title: 'Top Forms',
      submissions: 'Submissions',
      approvalRate: 'Approval Rate',
      avgTime: 'Avg Time',
    },
    charts: {
      submissionTrend: 'Submission Trend',
      categoryDistribution: 'Category Distribution',
      submissions: 'Submissions',
      approvals: 'Approvals',
      rejections: 'Rejections',
    },
    loading: 'Loading...',
    noData: 'No Data',
  },
  
  // Messages
  messages: {
    saveSuccess: 'Saved successfully',
    saveFailed: 'Failed to save',
    deleteSuccess: 'Deleted successfully',
    deleteFailed: 'Failed to delete',
    submitSuccess: 'Submitted successfully',
    submitFailed: 'Failed to submit',
    loadFailed: 'Failed to load',
    operationSuccess: 'Operation successful',
    operationFailed: 'Operation failed',
    formCreated: 'Form created successfully',
    formArchived: 'Form archived',
    formPublished: 'Form published',
    draftCannotToggle: 'Draft status cannot be toggled directly',
    versionPublished: 'Version published',
    versionDeprecated: 'Version deprecated',
    reviewSubmitted: 'Submitted for review',
    reviewApproved: 'Version approved',
    reviewRejected: 'Version rejected',
    publishSuccess: 'Published successfully! Form is now live',
    translationSaved: 'Translation saved',
    translationDeleted: 'Translation deleted',
    importSuccess: 'Import successful',
    fillRequired: 'Please fill in all required fields',
  },
  
  // Region
  region: {
    CN: 'China',
    US: 'United States',
    ME: 'Middle East',
    all: 'All Regions',
  },
};
